import React, { useState, useEffect } from 'react';
import { usePrayer } from '../../contexts/PrayerContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import SEO from '../common/SEO';

const QiblaPage = () => {
  const { location } = usePrayer();
  const { isDarkMode, isRTL } = useTheme();
  const { t } = useTranslation();

  const [heading, setHeading] = useState(null);
  const [qiblaDirection, setQiblaDirection] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState('prompt');

  useEffect(() => {
    if (!location) return;
    const { latitude, longitude } = location;
    const makkahLat = 21.4225;
    const makkahLng = 39.8262;
    const lat1 = latitude * (Math.PI / 180);
    const lat2 = makkahLat * (Math.PI / 180);
    const lngDiff = (makkahLng - longitude) * (Math.PI / 180);
    const y = Math.sin(lngDiff);
    const x = Math.cos(lat1) * Math.tan(lat2) - Math.sin(lat1) * Math.cos(lngDiff);
    let qibla = Math.atan2(y, x) * (180 / Math.PI);
    qibla = (qibla + 360) % 360;
    setQiblaDirection(qibla);
  }, [location]);

  useEffect(() => {
    const handleOrientation = (event) => {
      let compassHeading;
      if (event.webkitCompassHeading !== undefined) {
        compassHeading = event.webkitCompassHeading;
      } else if (event.alpha !== null) {
        compassHeading = 360 - event.alpha;
      }
      if (compassHeading !== undefined) setHeading(compassHeading);
    };

    const requestPermission = async () => {
      if (typeof DeviceOrientationEvent !== 'undefined' &&
          typeof DeviceOrientationEvent.requestPermission === 'function') {
        try {
          const permission = await DeviceOrientationEvent.requestPermission();
          setPermissionStatus(permission);
          if (permission === 'granted') {
            window.addEventListener('deviceorientation', handleOrientation, true);
          }
        } catch (err) {
          console.error('Permission request failed');
        }
      } else {
        window.addEventListener('deviceorientation', handleOrientation, true);
        setPermissionStatus('granted');
      }
    };

    requestPermission();
    return () => window.removeEventListener('deviceorientation', handleOrientation, true);
  }, []);

  const requestCompassPermission = async () => {
    if (typeof DeviceOrientationEvent !== 'undefined' &&
        typeof DeviceOrientationEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceOrientationEvent.requestPermission();
        setPermissionStatus(permission);
      } catch (err) {
        console.error('Could not access compass');
      }
    }
  };

  const compassRotation = heading !== null ? -heading : 0;
  const qiblaRotation = qiblaDirection !== null && heading !== null
    ? qiblaDirection - heading
    : qiblaDirection || 0;
  const isPointingToQibla = heading !== null && qiblaDirection !== null &&
    Math.abs(((qiblaDirection - heading + 180) % 360) - 180) < 10;

  return (
    <>
      <SEO
        title={t('qibla.qiblaDirection')}
        description="Find accurate Qibla direction with smart compass"
        path="/qibla"
      />
      <div
        className={`min-h-screen ${
          isDarkMode
            ? 'bg-gradient-to-b from-[var(--bg-dark-via)] via-[var(--bg-dark-from)] to-[var(--bg-dark-anchor)]'
            : 'bg-gradient-to-b from-[var(--bg-light-via)] via-[var(--bg-light-from)] to-white'
        }`}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="px-4 pt-4 pb-4">
          <h1 className={`text-2xl font-bold text-center ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>
            {t('qibla.qiblaDirection')}
          </h1>
          <p className={`text-center text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {location?.city}, {location?.country}
          </p>
        </div>

        <div className="px-4">
          <div className={`rounded-3xl p-6 ${
            isDarkMode
              ? 'bg-gradient-to-br from-[var(--card-dark-from)] to-[var(--card-dark-to)] shadow-xl'
              : 'bg-white shadow-xl shadow-sky-200/50'
          }`}>
            {/* Compass */}
            <div className="relative w-72 h-72 mx-auto">
              <div
                className="absolute inset-0 transition-transform duration-100 ease-out"
                style={{ transform: `rotate(${compassRotation}deg)` }}
              >
                {['N', 'E', 'S', 'W'].map((dir, i) => (
                  <div
                    key={dir}
                    className="absolute w-full h-full flex items-start justify-center"
                    style={{ transform: `rotate(${i * 90}deg)` }}
                  >
                    <span className={`mt-2 text-sm font-bold ${
                      dir === 'N' ? 'text-red-500' : isDarkMode ? 'text-gray-400' : 'text-gray-400'
                    }`}>{dir}</span>
                  </div>
                ))}
                <div className={`absolute inset-4 rounded-full border-2 ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`} />
                <div className={`absolute inset-12 rounded-full border ${isDarkMode ? 'border-white/5' : 'border-gray-100'}`} />
              </div>

              <div
                className="absolute inset-0 transition-transform duration-100 ease-out"
                style={{ transform: `rotate(${qiblaRotation}deg)` }}
              >
                <div className="absolute left-1/2 -translate-x-1/2 top-4 flex flex-col items-center">
                  <div className={`w-1 h-24 rounded-full ${
                    isPointingToQibla
                      ? 'bg-gradient-to-b from-emerald-400 to-emerald-600'
                      : 'bg-gradient-to-b from-amber-400 to-amber-600'
                  }`} />
                  <div className={`-mt-1 w-4 h-4 rotate-45 ${isPointingToQibla ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                </div>
              </div>

              <div className="absolute inset-0 flex items-center justify-center">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                  isPointingToQibla
                    ? 'bg-emerald-500 shadow-lg shadow-emerald-500/30'
                    : isDarkMode ? 'bg-white/10' : 'bg-gray-100'
                }`}>
                  <span className="text-2xl">🕋</span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="mt-8 text-center">
              {heading === null ? (
                <div className={`rounded-xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                  <AlertCircle className={`w-8 h-8 mx-auto mb-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {permissionStatus === 'denied' ? t('qibla.compassDenied') : t('qibla.calibrateCompass')}
                  </p>
                  {permissionStatus === 'prompt' && (
                    <Button
                      onClick={requestCompassPermission}
                      className="mt-3 bg-[var(--p-hex)] hover:bg-[var(--p-light-hex)] text-white"
                    >
                      {t('qibla.enableCompass')}
                    </Button>
                  )}
                </div>
              ) : isPointingToQibla ? (
                <div className="space-y-2">
                  <p className="text-xl font-bold text-emerald-500">{t('qibla.facingQibla')}</p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('qibla.makkahIs')} {Math.round(qiblaDirection)}° {t('qibla.fromNorth')}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}>
                    {qiblaRotation > 0 && qiblaRotation < 180 ? t('qibla.turnRight') : t('qibla.turnLeft')}
                  </p>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Qibla is {Math.round(qiblaDirection)}° {t('qibla.fromNorth')}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className={`mt-4 rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <h3 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              {t('qibla.howToUse')}
            </h3>
            <ol className={`text-sm space-y-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ${isRTL ? 'pr-4' : 'pl-4'}`}>
              {[
                t('qibla.step1EnableCompass'),
                t('qibla.step2RefreshPage'),
                t('qibla.step3CalibrateCompass')
              ].map((step, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
                  }`}>{idx + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className={`mt-4 rounded-2xl p-4 ${isDarkMode ? 'bg-white/5' : 'bg-white shadow-sm'}`}>
            <p className={`text-sm text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('qibla.bestResults')}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default QiblaPage;
